import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Settings, Send, Bot, User, Sparkles } from 'lucide-react';
import { apiClient } from '@/services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { TypingIndicator } from '@/components/ui/typing-indicator';

export function AIPanel() {
  const [messages, setMessages] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [config, setConfig] = useState<any>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(20);
  const scrollRef = useRef<HTMLDivElement>(null);

  const quickReplies = [
    "Analyze EUR/USD",
    "What's my current risk?",
    "Find trading opportunities"
  ];

  const fetchConfig = async () => {
    try {
      const res = await apiClient.get('/config');
      setConfig(res.data);
    } catch (err) {
      console.error('Failed to fetch AI config', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res: any = await apiClient.getChatHistory();
      const history = Array.isArray(res) ? res : res?.messages || [];
      // Only keep user and ai messages
      setMessages(history.filter((m: any) => m.role === 'user' || m.role === 'ai'));
    } catch (err) {
      console.error('Failed to fetch chat history', err);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop === 0) {
      if (displayLimit < messages.length) {
        // Save current scroll height to restore position after render
        const scrollHeight = e.currentTarget.scrollHeight;
        setDisplayLimit(prev => Math.min(prev + 20, messages.length));
        
        // Use a timeout to restore scroll position after DOM updates
        setTimeout(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight - scrollHeight;
          }
        }, 0);
      }
    }
  };

  const handleSend = async (text: string) => {
    if (!text.trim()) return;

    const newMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, newMsg]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response: any = await apiClient.sendMessage(text);
      if (response && response.data) {
        setMessages(prev => [...prev, response.data]);
      } else {
        fetchHistory(); // Fallback if format is unexpected
      }
    } catch (err) {
      console.error('Message failed', err);
      // Optional: show error message
    } finally {
      setIsTyping(false);
    }
  };

  const switchProvider = async (provider: string) => {
    try {
      await apiClient.post('/config', { provider });
      await fetchConfig();
      setShowConfig(false);
    } catch (err) {
      console.error('Failed to switch provider', err);
    }
  };

  const displayedMessages = messages.slice(-displayLimit);

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-[600px] overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-border/50 shrink-0 bg-background/95 sticky top-0 z-10">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold text-foreground">AI Assistant</CardTitle>
            <div className="text-[10px] text-muted-foreground flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              {config?.provider?.toUpperCase() || 'Online'}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent rounded-full transition-colors" onClick={() => setShowConfig(!showConfig)}>
          <Settings className="w-4 h-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="flex-1 p-0 flex flex-col overflow-hidden relative">
        {showConfig ? (
          <div className="p-4 space-y-4 absolute inset-0 bg-background/95 backdrop-blur-md z-20 overflow-y-auto">
            <h3 className="text-sm font-medium">AI Model Provider</h3>
            <div className="space-y-2">
              {['auto', 'claude', 'gemini', 'groq', 'openrouter', 'huggingface'].map(provider => (
                <Button 
                  key={provider}
                  variant={config?.provider === provider ? 'default' : 'outline'}
                  className={`w-full justify-start capitalize ${config?.provider === provider ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20' : ''}`}
                  onClick={() => switchProvider(provider)}
                >
                  {provider} {config?.provider === provider && '(Active)'}
                </Button>
              ))}
            </div>
            <Button variant="ghost" className="w-full mt-4" onClick={() => setShowConfig(false)}>Close Settings</Button>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar" ref={scrollRef} onScroll={handleScroll}>
          {messages.length === 0 && !isTyping ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-70">
              <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center">
                <Bot className="w-8 h-8 text-primary/50" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">How can I help you trade today?</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-[200px] mx-auto">Ask me about markets, risk analysis, or specific instruments.</p>
              </div>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {displayLimit < messages.length && (
                <div className="text-center py-2">
                  <span className="text-[10px] text-muted-foreground bg-accent/50 px-2 py-1 rounded-full cursor-pointer hover:bg-accent transition-colors" onClick={() => setDisplayLimit(prev => Math.min(prev + 20, messages.length))}>
                    Scroll up to load older messages
                  </span>
                </div>
              )}
              {displayedMessages.map((msg, idx) => (
                <motion.div
                  key={msg.id || idx}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'ai' && (
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mb-1">
                      <Bot className="w-3.5 h-3.5 text-primary" />
                    </div>
                  )}
                  
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed shadow-sm ${
                    msg.role === 'user' 
                      ? 'bg-primary text-primary-foreground rounded-br-sm' 
                      : 'bg-muted border border-border/50 text-foreground rounded-bl-sm'
                  }`}>
                    {msg.content}
                  </div>

                  {msg.role === 'user' && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mb-1">
                      <User className="w-3.5 h-3.5 text-primary-foreground" />
                    </div>
                  )}
                </motion.div>
              ))}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <TypingIndicator avatarSize="sm" />
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>

        {/* Quick Replies */}
        {messages.length < 2 && !isTyping && !showConfig && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-4 pb-2 flex flex-wrap gap-2"
          >
            {quickReplies.map((reply, i) => (
              <Button 
                key={i} 
                variant="outline" 
                size="sm" 
                className="text-[11px] h-7 rounded-full bg-background/50 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
                onClick={() => handleSend(reply)}
              >
                {reply}
              </Button>
            ))}
          </motion.div>
        )}

        <form 
          onSubmit={(e) => { e.preventDefault(); handleSend(inputValue); }} 
          className="p-3 bg-background/80 border-t border-border/50 backdrop-blur-sm shrink-0"
        >
          <div className="relative flex items-center">
            <Input 
              placeholder="Message AI Assistant..." 
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="pr-10 rounded-full bg-background border-border focus-visible:ring-primary/30 h-10 text-[13px]"
              disabled={isTyping}
            />
            <Button 
              type="submit" 
              size="icon"
              disabled={!inputValue.trim() || isTyping}
              className={`absolute right-1 w-8 h-8 rounded-full transition-all duration-300 ${
                inputValue.trim() ? 'bg-primary text-primary-foreground shadow-md hover:scale-105' : 'bg-transparent text-muted-foreground hover:bg-accent'
              }`}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
